import { Subject, takeUntil } from 'rxjs';

import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { LongNumberValue } from '../../validations';

/**
 * A field for a 64-bit proto number, which arrives as a string and is read back as a number
 * when one can hold it exactly. Ordinary numbers need none of that, and use a plain input.
 */
@Component({
  selector: 'phoenix-long-number-control',
  template: `
    <mat-form-field class="w-full" [subscriptSizing]="subscriptSizing">
      <mat-label>{{ label }}</mat-label>
      <input
        matInput
        type="number"
        autocomplete="off"
        [formControl]="control"
        [readonly]="readonly"
        [attr.aria-label]="ariaLabel"
      />
      @if (suffix) {
        <span matTextSuffix>{{ suffix }}</span>
      }
      <mat-error
        ><small>{{ control | formError }}</small></mat-error
      >
    </mat-form-field>
  `,
  standalone: false,
})
export class LongNumberControlComponent implements OnChanges, OnDestroy {
  @Input({ required: true }) control = new FormControl<LongNumberValue>(null);
  @Input() label = '';
  @Input() suffix = '';
  @Input() ariaLabel = '';
  @Input() readonly = false;
  @Input() subscriptSizing: 'fixed' | 'dynamic' = 'fixed';

  private readonly destroy$ = new Subject<void>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['control']) return;

    this.destroy$.next();
    this.readAsNumber();

    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.readAsNumber());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Same number, told as one. Silent and untouched-preserving: it settles what a patch left
   * behind, and typing already produces numbers.
   */
  private readAsNumber(): void {
    const value = this.control.value;

    if (typeof value !== 'string' || value.trim() === '') return;

    const trimmed = value.trim();
    const parsed = Number(trimmed);

    if (!Number.isFinite(parsed)) return;

    // A 64-bit field can hold more than a number carries exactly. Reading it would round it,
    // and the rounded value is one the proto then refuses, so it is left as it arrived.
    if (/^-?\d+$/.test(trimmed) && !Number.isSafeInteger(parsed)) return;

    this.control.setValue(parsed, { emitEvent: false });
  }
}
